import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../config/site";
import { Logo } from "./Logo";
import { UrgentButton } from "./UrgentModal";
import { EASE } from "./Reveal";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="fixed inset-x-0 top-0 z-50 border-b border-slate-200/70 bg-white/90 backdrop-blur-md"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" aria-label="SwiftTest Solutions home" onClick={() => setOpen(false)}>
          <Logo />
        </Link>
        <nav data-testid="desktop-nav" className="hidden items-center gap-1 lg:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-3.5 py-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-teal-50 text-teal-800"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <UrgentButton className="hidden sm:inline-flex" />
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className="rounded-full p-2.5 text-slate-700 transition-colors hover:bg-slate-100 lg:hidden"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            key="mobile-nav"
            data-testid="mobile-nav"
            className="border-t border-slate-100 bg-white/95 px-5 pb-6 pt-3 backdrop-blur-md lg:hidden"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            style={{ overflow: "hidden" }}
          >
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.path}
                to={l.path}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={() =>
                  `block rounded-xl px-4 py-3 text-base font-semibold transition-colors ${
                    location.pathname === l.path
                      ? "bg-teal-50 text-teal-800"
                      : "text-slate-700 hover:bg-slate-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-3 px-1 sm:hidden">
              <UrgentButton className="w-full justify-center" testId="need-testing-now-button-mobile" />
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
